import { TDevice } from '@cian/utils';
import { ILogger } from '@cian/logger/shared';
import { IConfig } from '@cian/config/shared';
import { IHttpApi } from '@cian/http-api/shared/http';
import { UiKitContextProvider } from '@cian/ui-kit';
import {
  Authentication,
  createContext,
  EEventType,
  ESentEmailType,
  IAuthenticationContext,
  IState as IAuthenticationWidgetState,
  Provider,
} from '@cian/authentication-widget';
import * as React from 'react';

import { trackShow } from './tracking';

export interface IAuthWidgetProps {
  httpApi: IHttpApi;
  logger: ILogger;
  config: IConfig;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  authService: any;
  subdomain: string;
  disabledSocials: string[];
  deviceType: TDevice | undefined;
  preloadedState: Partial<IAuthenticationWidgetState>;
}

interface IAppState {
  authContext: IAuthenticationContext;
}

export class AuthWidget extends React.Component<IAuthWidgetProps, IAppState> {
  public constructor(props: IAuthWidgetProps) {
    super(props);

    const { httpApi, logger, subdomain, config, preloadedState } = props;

    this.state = {
      authContext: createContext(
        {
          httpApi,
          logger,
          subdomain,
          config,
        },
        preloadedState,
      ),
    };
  }

  public componentDidMount(): void {
    this.state.authContext.on(EEventType.AuthenticateSucceed, this.authenticationSucceed);
    this.state.authContext.on(EEventType.RegisterSucceed, this.handleRegisterSucceed);
    this.state.authContext.on(EEventType.SocialAuthenticateSucceed, this.handleSocialAuthenticateSucceed);
    this.state.authContext.on(EEventType.EmailSentClosed, this.handleEmailSentClosed);
    trackShow();
  }

  public componentWillUnmount(): void {
    this.state.authContext.off(EEventType.AuthenticateSucceed, this.authenticationSucceed);
    this.state.authContext.off(EEventType.RegisterSucceed, this.handleRegisterSucceed);
    this.state.authContext.off(EEventType.SocialAuthenticateSucceed, this.handleSocialAuthenticateSucceed);
    this.state.authContext.off(EEventType.EmailSentClosed, this.handleEmailSentClosed);
  }

  public render() {
    const { deviceType, disabledSocials } = this.props;

    return (
      <UiKitContextProvider deviceType={deviceType}>
        <Provider context={this.state.authContext}>
          <Authentication disabledSocials={disabledSocials} />
        </Provider>
      </UiKitContextProvider>
    );
  }

  public handleSocialAuthenticateSucceed = ({ isNewUser }: { isNewUser: boolean }) => {
    if (!isNewUser) {
      this.authenticationSucceed();
    }
  };

  public handleRegisterSucceed = (event?: { senderType: string }) => {
    if (event?.senderType) {
      this.authenticationSucceed();
    }
  };

  public handleEmailSentClosed = ({ type }: { type: ESentEmailType }) => {
    if (type === ESentEmailType.Registration) {
      this.authenticationSucceed();
    }
  };

  public authenticationSucceed = () => {
    this.props.authService.getUserData();
  };
}
